/**
 * Everything about the business, configured once: its master data, its plan, the chart of accounts
 * and who else may see the books.
 *
 * The master data is not decoration - the § 11 checklist on every outgoing invoice checks against
 * exactly these fields, and the VAT scheme decides whether a UVA is computed at all.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Archive, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { EmptyState } from "@/components/shared/empty-state";
import { AccountingExportSettings } from "@/components/settings/accounting-export-settings";
import { InboxSettings } from "@/components/settings/inbox-settings";
import { PlanSettings } from "@/components/settings/plan-settings";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCategories } from "@/hooks/use-documents";
import {
  useInviteMember,
  useInvites,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
  useUpdateMemberRole,
  useUpdateWorkspace,
} from "@/hooks/use-workspaces";
import { requestVerification } from "@/lib/api/auth";
import { archiveCategory, createCategory } from "@/lib/api/categories";
import { isValidAustrianVatId, normalizeVatId } from "@/lib/at/uid";
import { AUSTRIAN_VAT_RATES } from "@/lib/at/vat";
import { formatRate } from "@/lib/utils/format";
import { useAuth } from "@/lib/session/auth-context";
import { applyServerErrors } from "@/lib/utils/form-errors";
import { useWorkspace } from "@/lib/session/workspace-context";
import { useQueryClient } from "@tanstack/react-query";
import { DataSettings } from "@/components/settings/data-settings";

const ROLES = ["member", "accountant", "viewer"] as const;

export function SettingsPage() {
  const { t } = useTranslation();
  const { workspace, canManage } = useWorkspace();
  const [params, setParams] = useSearchParams();
  // The tab lives in the URL, so a link (the plan banner, an email) can open one directly.
  const tab = params.get("tab") ?? "business";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setParams({ tab: value }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="business" data-testid="tab-business">
            {t("settings.business")}
          </TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">
            {t("settings.plan")}
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            {t("settings.categories")}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            {t("settings.members")}
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            {t("settings.inbox")}
          </TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">
            {t("settings.export")}
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="data" data-testid="tab-data">
              {t("settings.data")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="business" className="pt-6">
          {workspace && <BusinessForm key={workspace.id} />}
        </TabsContent>
        <TabsContent value="plan" className="pt-6">
          <PlanSettings />
        </TabsContent>
        <TabsContent value="categories" className="pt-6">
          <CategoriesSection />
        </TabsContent>
        <TabsContent value="inbox" className="pt-6">
          <InboxSettings />
        </TabsContent>
        <TabsContent value="export" className="pt-6">
          {workspace && <AccountingExportSettings key={workspace.id} />}
        </TabsContent>
        {canManage && (
          <TabsContent value="data" className="pt-6">
            <DataSettings />
          </TabsContent>
        )}
        <TabsContent value="members" className="pt-6">
          {canManage ? (
            <MembersSection />
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="members-owner-only">
              {t("settings.membersOwnerOnly")}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BusinessForm() {
  const { t } = useTranslation();
  const { workspace, canManage } = useWorkspace();
  const updateWorkspace = useUpdateWorkspace();

  const schema = z.object({
    name: z.string().min(2, t("validation.workspaceName")).max(120),
    legalName: z.string().max(200),
    vatId: z
      .string()
      .refine((value) => value === "" || !/^AT/i.test(value) || isValidAustrianVatId(value), {
        message: t("validation.vatId"),
      }),
    taxNumber: z.string().max(30),
    street: z.string().max(200),
    zip: z.string().max(20),
    city: z.string().max(120),
    email: z.union([z.literal(""), z.email(t("validation.email"))]),
    iban: z.string().max(40),
    accountingBasis: z.enum(["cash", "accrual"]),
    vatScheme: z.enum(["standard", "small_business"]),
    vatPeriod: z.enum(["monthly", "quarterly", "none"]),
    smallBusinessLimit: z.number().min(0),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: workspace?.name ?? "",
      legalName: workspace?.legalName ?? "",
      vatId: workspace?.vatId ?? "",
      taxNumber: workspace?.taxNumber ?? "",
      street: workspace?.street ?? "",
      zip: workspace?.zip ?? "",
      city: workspace?.city ?? "",
      email: workspace?.email ?? "",
      iban: workspace?.iban ?? "",
      accountingBasis: workspace?.accountingBasis ?? "cash",
      vatScheme: workspace?.vatScheme ?? "standard",
      vatPeriod: workspace?.vatPeriod ?? "quarterly",
      smallBusinessLimit: workspace?.smallBusinessLimit || 55000,
    },
  });

  async function onSubmit(values: Values) {
    if (!workspace) return;
    try {
      await updateWorkspace.mutateAsync({
        id: workspace.id,
        input: { ...values, vatId: normalizeVatId(values.vatId) },
      });
      toast.success(t("settings.saved"));
    } catch (error) {
      const message = applyServerErrors(error, (name, err) =>
        form.setError(name as keyof Values, err),
      );
      if (message) toast.error(message);
    }
  }

  const text = (name: keyof Values, label: string, hint?: string, className?: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input disabled={!canManage} className={className} {...field} />
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form
        className="max-w-2xl space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
        data-testid="business-form"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {text("name", t("workspace.name"))}
          {text("legalName", t("workspace.legalName"), t("workspace.legalNameHint"))}
          {text("vatId", t("workspace.vatId"), t("workspace.vatIdHint"), "num")}
          {text("taxNumber", t("workspace.taxNumber"), undefined, "num")}
          {text("street", t("workspace.street"))}
          <div className="grid grid-cols-[6rem_1fr] gap-3">
            {text("zip", t("workspace.zip"), undefined, "num")}
            {text("city", t("workspace.city"))}
          </div>
          {text("email", t("workspace.email"))}
          {text("iban", t("workspace.iban"), undefined, "num")}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="accountingBasis"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("workspace.accountingBasis")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!canManage}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="cash">{t("accountingBasis.cash")}</SelectItem>
                    <SelectItem value="accrual">{t("accountingBasis.accrual")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  {t(`accountingBasisHint.${form.watch("accountingBasis")}`)}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="vatScheme"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("workspace.vatScheme")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!canManage}>
                  <FormControl>
                    <SelectTrigger data-testid="settings-vat-scheme">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="standard">{t("vatScheme.standard")}</SelectItem>
                    <SelectItem value="small_business">{t("vatScheme.small_business")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="vatPeriod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("workspace.vatPeriod")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!canManage}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="monthly">{t("vatPeriod.monthly")}</SelectItem>
                    <SelectItem value="quarterly">{t("vatPeriod.quarterly")}</SelectItem>
                    <SelectItem value="none">{t("vatPeriod.none")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>{t("workspace.vatPeriodHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {form.watch("vatScheme") === "small_business" && (
          <FormField
            control={form.control}
            name="smallBusinessLimit"
            render={({ field }) => (
              <FormItem className="max-w-56">
                <FormLabel>{t("workspace.smallBusinessLimit")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="num"
                    disabled={!canManage}
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(event) => field.onChange(event.target.valueAsNumber || 0)}
                  />
                </FormControl>
                <FormDescription>{t("workspace.smallBusinessLimitHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {canManage && (
          <Button type="submit" disabled={updateWorkspace.isPending} data-testid="save-business">
            {t("common.save")}
          </Button>
        )}
      </form>
    </Form>
  );
}

function CategoriesSection() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const { data: categories } = useCategories(workspace?.id);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    name: "",
    kind: "expense" as "expense" | "revenue",
    accountNumber: "",
    vatRate: 20,
  });
  const [saving, setSaving] = useState(false);

  async function add() {
    if (!workspace || draft.name.trim().length === 0) return;
    setSaving(true);
    try {
      await createCategory(workspace.id, { ...draft, deductiblePercent: 100 });
      await queryClient.invalidateQueries({ queryKey: ["categories", workspace.id] });
      setDraft({ name: "", kind: draft.kind, accountNumber: "", vatRate: 20 });
      toast.success(t("settings.categoryAdded"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("settings.categoryFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm text-muted-foreground">{t("settings.categoriesHint")}</p>

      {canWrite && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border p-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="cat-account">
              {t("settings.accountNumber")}
            </Label>
            <Input
              id="cat-account"
              className="w-24 num"
              value={draft.accountNumber}
              onChange={(event) => setDraft({ ...draft, accountNumber: event.target.value })}
            />
          </div>
          <div className="min-w-48 flex-1 space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="cat-name">
              {t("settings.categoryName")}
            </Label>
            <Input
              id="cat-name"
              data-testid="category-name"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="cat-kind">
              {t("settings.categoryKind")}
            </Label>
            <Select
              value={draft.kind}
              onValueChange={(value) => setDraft({ ...draft, kind: value as never })}
            >
              <SelectTrigger id="cat-kind" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">{t("settings.expense")}</SelectItem>
                <SelectItem value="revenue">{t("settings.revenue")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="cat-rate">
              {t("document.rate")}
            </Label>
            <Select
              value={String(draft.vatRate)}
              onValueChange={(value) => setDraft({ ...draft, vatRate: Number(value) })}
            >
              <SelectTrigger id="cat-rate" className="w-24 num">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUSTRIAN_VAT_RATES.map((rate) => (
                  <SelectItem key={rate} value={String(rate)}>
                    {formatRate(rate)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => void add()} disabled={saving} data-testid="add-category">
            <Plus /> {t("common.add")}
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border">
        <Table data-testid="categories-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t("settings.accountNumber")}</TableHead>
              <TableHead>{t("settings.categoryName")}</TableHead>
              <TableHead className="hidden sm:table-cell">{t("settings.categoryKind")}</TableHead>
              <TableHead className="text-right">{t("document.rate")}</TableHead>
              <TableHead className="text-right">{t("document.deductiblePercent")}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(categories ?? []).map((category) => (
              <TableRow key={category.id} data-testid="category-row">
                <TableCell className="ledger-id">{category.accountNumber}</TableCell>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell className="hidden text-muted-foreground sm:table-cell">
                  {t(`settings.${category.kind}`)}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap num">
                  {category.vatRate} %
                </TableCell>
                <TableCell className="text-right whitespace-nowrap num">
                  {category.deductiblePercent} %
                </TableCell>
                <TableCell>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("settings.archiveCategory", { name: category.name })}
                      onClick={async () => {
                        await archiveCategory(category.id);
                        await queryClient.invalidateQueries({
                          queryKey: ["categories", workspace?.id],
                        });
                        toast.success(t("settings.categoryArchived"));
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MembersSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { data: members } = useMembers(workspace?.id);
  const { data: invites } = useInvites(workspace?.id);
  const invite = useInviteMember(workspace?.id ?? "", user?.id ?? "");
  const revoke = useRevokeInvite();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<(typeof ROLES)[number]>("accountant");
  const [error, setError] = useState("");

  async function send() {
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(t("validation.email"));
      return;
    }
    try {
      await invite.mutateAsync({ email, role });
      setEmail("");
      toast.success(t("settings.inviteSent", { email }));
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t("settings.inviteFailed"));
    }
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div className="space-y-3">
        <div>
          <h2 className="font-medium">{t("settings.inviteTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("settings.inviteHint")}</p>
        </div>
        {/* The server refuses invitations from unconfirmed addresses (finding 17). */}
        {user && !user.verified && (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2 text-sm"
            data-testid="verify-first"
          >
            <p className="min-w-0 flex-1">{t("settings.verifyFirst")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void requestVerification(user.email).then(
                  () => toast.success(t("settings.verificationSent", { email: user.email })),
                  (caught: Error) => toast.error(caught.message),
                )
              }
            >
              {t("settings.resendVerification")}
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1 space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="invite-email">
              {t("login.email")}
            </Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              data-testid="invite-email"
              onChange={(event) => {
                setEmail(event.target.value);
                setError("");
              }}
            />
            {error && (
              <p role="alert" className="text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground" htmlFor="invite-role">
              {t("settings.role")}
            </Label>
            <Select value={role} onValueChange={(value) => setRole(value as never)}>
              <SelectTrigger id="invite-role" className="w-44" data-testid="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((entry) => (
                  <SelectItem key={entry} value={entry}>
                    {t(`roles.${entry}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => void send()} disabled={invite.isPending} data-testid="send-invite">
            {t("settings.sendInvite")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t(`roleHints.${role}`)}</p>
      </div>

      <div className="space-y-2">
        <h2 className="font-medium">{t("settings.members")}</h2>
        <div className="overflow-hidden rounded-lg border">
          <Table data-testid="members-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.member")}</TableHead>
                <TableHead>{t("settings.role")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(members ?? []).map((member) => (
                <TableRow key={member.id} data-testid="member-row">
                  {/* Takes what the role and the button leave; a long address wraps. */}
                  <TableCell className="w-full max-w-0">
                    <p className="font-medium wrap-anywhere">
                      {member.expand?.user?.name || member.email}
                    </p>
                    <p className="text-xs wrap-anywhere text-muted-foreground">{member.email}</p>
                  </TableCell>
                  <TableCell>
                    {member.role === "owner" ? (
                      <span className="text-sm">{t("roles.owner")}</span>
                    ) : (
                      <Select
                        value={member.role}
                        onValueChange={(value) =>
                          updateRole.mutate({ id: member.id, role: value as never })
                        }
                      >
                        <SelectTrigger className="w-auto sm:w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((entry) => (
                            <SelectItem key={entry} value={entry}>
                              {t(`roles.${entry}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    {member.role !== "owner" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("settings.removeMember", { email: member.email })}
                        onClick={() => removeMember.mutate(member.id)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {invites && invites.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-medium">{t("settings.pendingInvites")}</h2>
          <ul className="divide-y rounded-lg border bg-card" data-testid="pending-invites">
            {invites.map((entry) => (
              <li key={entry.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="min-w-0 font-medium wrap-anywhere">{entry.email}</span>
                <span className="shrink-0 text-muted-foreground">{t(`roles.${entry.role}`)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto shrink-0"
                  onClick={() => revoke.mutate(entry.id)}
                >
                  {t("settings.revoke")}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {members && members.length === 1 && (!invites || invites.length === 0) && (
        <EmptyState title={t("settings.aloneTitle")} description={t("settings.aloneText")} />
      )}
    </div>
  );
}
